import type { TranslationBundle } from '@bms/common';

/**
 * The guided check-in (`components/checkin/`): CheckInDialog's three steps and
 * KeyEncodeStep. The stay editor keeps `housekeeping.stay.*` in common's tables.
 */
export const checkIn: TranslationBundle = {
  en: {
    title: 'Check in',
    titleRoom: 'Check in — {{room}}',
    stepOf: 'Step {{step}} of {{total}} · {{name}}',
    step: { guest: 'Guest', keys: 'Keys', done: 'Done' },
    room: 'Room',
    pickRoom: 'Pick a ready room',
    noReadyRoom: 'No room is ready to sell right now.',
    guestName: 'Guest name',
    guestPlaceholder: 'Guest name (optional)',
    nights: 'Nights',
    nightsSummary_one: '{{count}} night · out {{checkout}}',
    nightsSummary_other: '{{count}} nights · out {{checkout}}',
    checkoutSummary: 'Out {{checkout}}',
    changeTime: 'Different checkout…',
    checkoutAria: 'Expected checkout (local time)',
    useHouseTime: 'Use the house checkout time',
    keyCount: 'Keys',
    keyCountAria_one: '{{count}} key',
    keyCountAria_other: '{{count}} keys',
    continueToKeys_one: 'Check in · {{count}} key',
    continueToKeys_other: 'Check in · {{count}} keys',
    checkInOnly: 'Check in',
    failed: 'Check-in failed',
    notReady: {
      warning: 'This room is not Ready: its setup is unfinished, so its keys may not open the door.',
      confirm: 'Check in anyway',
    },
    reader: {
      ready: 'Reader ready. Place a card on it.',
      readyWithCard: 'Reader ready · card {{uid}} is on it.',
      disconnected: 'No reader: the card agent is not running on this PC, so keys cannot be written here.',
      noSam: 'The reader has no SAM, so it cannot write keys.',
      notReady: 'The reader cannot write keys: {{reason}}.',
    },
    keys: {
      placeCard: 'Place key {{n}} of {{total}} on the reader.',
      placeReplacement: 'Place the new card for key {{n}} on the reader.',
      writing: 'Encoding key {{n}} of {{total}} · {{uid}}…',
      swapCard: 'Key {{n}} of {{total}} is written. Take it off and place the next card.',
      unsupportedCard: 'Card {{uid}} cannot be a guest key: its chip type is not supported.',
      notReadyHint: 'The guest is checked in. Finish now and encode the keys later from the room dialog.',
      writtenAria: 'Keys written',
      keyWritten: 'Key {{n}} ✓',
      keyPending: 'Key {{n}} not confirmed. Tap it on the reader before handing it out.',
      retry: 'Retry',
      retryNeedsCard: 'Place the card back on the reader.',
      finishWithoutKey: 'Finish without a key',
      finishWith_one: 'Finish with {{count}} key',
      finishWith_other: 'Finish with {{count}} keys',
    },
    done: {
      guestIn: '{{guest}} is checked in to {{room}}.',
      roomIn: '{{room}} is checked in.',
      noKeys: 'No key was encoded.',
      keys_one: '{{count}} key encoded.',
      keys_other: '{{count}} keys encoded.',
      keysPending: 'A key whose write was not confirmed stays pending: tap it at the reader before handing it out.',
      checkout: 'Checkout {{checkout}}.',
    },
    // The room dialog's Add key and Replace (F10): the Keys step for one card.
    addKey: {
      title: 'Add key — {{room}}',
      replaceTitle: 'Replace key {{n}} — {{room}}',
      validUntil: 'valid until {{date}}',
      added: 'Key {{n}} encoded.',
      replaced: 'Key {{n}} replaced. The old key ****{{card}} is revoked as lost.',
      another: 'Add another',
    },
  },
  hr: {
    title: 'Prijava gosta',
    titleRoom: 'Prijava gosta — {{room}}',
    stepOf: 'Korak {{step}} od {{total}} · {{name}}',
    step: { guest: 'Gost', keys: 'Ključevi', done: 'Gotovo' },
    room: 'Soba',
    pickRoom: 'Odaberite spremnu sobu',
    noReadyRoom: 'Trenutačno nijedna soba nije spremna za prodaju.',
    guestName: 'Ime gosta',
    guestPlaceholder: 'Ime gosta (neobavezno)',
    nights: 'Noćenja',
    nightsSummary_one: '{{count}} noćenje · odjava {{checkout}}',
    nightsSummary_few: '{{count}} noćenja · odjava {{checkout}}',
    nightsSummary_other: '{{count}} noćenja · odjava {{checkout}}',
    checkoutSummary: 'Odjava {{checkout}}',
    changeTime: 'Druga odjava…',
    checkoutAria: 'Očekivana odjava (lokalno vrijeme)',
    useHouseTime: 'Koristi vrijeme odjave hotela',
    keyCount: 'Ključevi',
    keyCountAria_one: '{{count}} ključ',
    keyCountAria_few: '{{count}} ključa',
    keyCountAria_other: '{{count}} ključeva',
    continueToKeys_one: 'Prijavi · {{count}} ključ',
    continueToKeys_few: 'Prijavi · {{count}} ključa',
    continueToKeys_other: 'Prijavi · {{count}} ključeva',
    checkInOnly: 'Prijavi',
    failed: 'Prijava nije uspjela',
    notReady: {
      warning: 'Ova soba nije spremna: njezino postavljanje nije dovršeno, pa njezini ključevi možda neće otvoriti vrata.',
      confirm: 'Svejedno prijavi',
    },
    reader: {
      ready: 'Čitač je spreman. Stavite karticu na njega.',
      readyWithCard: 'Čitač je spreman · kartica {{uid}} je na njemu.',
      disconnected: 'Nema čitača: agent za kartice ne radi na ovom računalu, pa se ključevi ovdje ne mogu zapisati.',
      noSam: 'Čitač nema SAM, pa ne može zapisivati ključeve.',
      notReady: 'Čitač ne može zapisivati ključeve: {{reason}}.',
    },
    keys: {
      placeCard: 'Stavite ključ {{n}} od {{total}} na čitač.',
      placeReplacement: 'Stavite novu karticu za ključ {{n}} na čitač.',
      writing: 'Kodiranje ključa {{n}} od {{total}} · {{uid}}…',
      swapCard: 'Ključ {{n}} od {{total}} je zapisan. Maknite ga i stavite sljedeću karticu.',
      unsupportedCard: 'Kartica {{uid}} ne može biti ključ gosta: njezina vrsta čipa nije podržana.',
      notReadyHint: 'Gost je prijavljen. Završite sada, a ključeve kodirajte kasnije iz prozora sobe.',
      writtenAria: 'Zapisani ključevi',
      keyWritten: 'Ključ {{n}} ✓',
      keyPending: 'Ključ {{n}} nije potvrđen. Prislonite ga na čitač prije predaje.',
      retry: 'Pokušaj ponovno',
      retryNeedsCard: 'Vratite karticu na čitač.',
      finishWithoutKey: 'Završi bez ključa',
      finishWith_one: 'Završi s {{count}} ključem',
      finishWith_few: 'Završi s {{count}} ključa',
      finishWith_other: 'Završi s {{count}} ključeva',
    },
    done: {
      guestIn: '{{guest}} je prijavljen/a u sobu {{room}}.',
      roomIn: 'Soba {{room}} je prijavljena.',
      noKeys: 'Nijedan ključ nije kodiran.',
      keys_one: 'Kodiran {{count}} ključ.',
      keys_few: 'Kodirana {{count}} ključa.',
      keys_other: 'Kodirano {{count}} ključeva.',
      keysPending: 'Ključ čiji zapis nije potvrđen ostaje na čekanju: prislonite ga na čitač prije predaje.',
      checkout: 'Odjava {{checkout}}.',
    },
    addKey: {
      title: 'Dodaj ključ — {{room}}',
      replaceTitle: 'Zamijeni ključ {{n}} — {{room}}',
      validUntil: 'vrijedi do {{date}}',
      added: 'Ključ {{n}} je kodiran.',
      replaced: 'Ključ {{n}} je zamijenjen. Stari ključ ****{{card}} opozvan je kao izgubljen.',
      another: 'Dodaj još jedan',
    },
  },
};
